/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { CaretRightOutlined, CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { extractVariants, VariantInfo } from './schemaUtils';
import { SchemaViewer } from './SchemaViewer';
import { TryItPanel } from './TryItPanel';
import { Endpoint } from './types';

interface EndpointDetailProps {
  endpoint: Endpoint;
  schemas: Record<string, unknown>;
  resolveSchema: (schema: Record<string, unknown>) => Record<string, unknown>;
  authToken: string | null;
  tenantID?: string;
  subtenantID?: string;
}

function getResponseSchema(
  response: { content?: Record<string, { schema?: Record<string, unknown> }> },
  resolve: EndpointDetailProps['resolveSchema'],
) {
  const schema = response.content?.['application/json']?.schema;
  return schema ? resolve(schema) : null;
}

function statusClass(code: string) {
  return `apiref-status s${code[0]}`;
}

export function EndpointDetail({
  endpoint,
  schemas,
  resolveSchema: resolve,
  authToken,
  tenantID,
  subtenantID,
}: EndpointDetailProps) {
  const rawBodySchema = endpoint.requestBody?.content?.['application/json']?.schema || null;
  const pathParams = endpoint.parameters?.filter((p) => p.in === 'path') || [];
  const queryParams = endpoint.parameters?.filter((p) => p.in === 'query') || [];

  // Variant support for oneOf/anyOf schemas
  const variants: VariantInfo[] | null = useMemo(
    () => (rawBodySchema ? extractVariants(rawBodySchema, schemas) : null),
    [rawBodySchema, schemas],
  );
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(0);

  // Reset variant selection when endpoint changes
  useEffect(() => {
    setSelectedVariantIndex(0);
  }, [endpoint.operationId, endpoint.method, endpoint.path]);

  const bodySchema = useMemo(() => {
    if (variants) {
      return variants[selectedVariantIndex]?.schema || null;
    }
    if (rawBodySchema) {
      return resolve(rawBodySchema);
    }
    return null;
  }, [variants, selectedVariantIndex, rawBodySchema, resolve]);

  const [curlCommand, setCurlCommand] = useState('');
  const [curlCopied, setCurlCopied] = useState(false);

  const handleCurlChange = useCallback((curl: string) => {
    setCurlCommand(curl);
  }, []);

  const handleCopyCurl = () => {
    navigator.clipboard.writeText(curlCommand).then(
      () => {
        setCurlCopied(true);
        setTimeout(() => setCurlCopied(false), 2000);
      },
      () => {},
    );
  };

  const responses2xx = Object.entries(endpoint.responses || {}).filter(([code]) => code.startsWith('2'));
  const responsesOther = Object.entries(endpoint.responses || {}).filter(([code]) => !code.startsWith('2'));

  return (
    <div className="apiref-detail">
      <div className="apiref-detail-header">
        <span className={`apiref-detail-method bg-${endpoint.method}`}>{endpoint.method.toUpperCase()}</span>
        <code className="apiref-detail-path">{endpoint.path}</code>
      </div>

      <div className="apiref-detail-meta">
        {endpoint.permission && (
          <span className="apiref-perm">
            <span className="apiref-perm-label">Permission:</span> {endpoint.permission}
          </span>
        )}
      </div>

      {endpoint.description && <p className="apiref-detail-desc">{endpoint.description}</p>}

      {variants && variants.length > 1 && (
        <div className="apiref-variant-selector">
          <label className="apiref-variant-label" htmlFor="apiref-variant-select">
            Schema variant
          </label>
          <select
            id="apiref-variant-select"
            className="apiref-variant-select"
            value={selectedVariantIndex}
            onChange={(e) => setSelectedVariantIndex(Number(e.target.value))}
          >
            {variants.map((v, i) => (
              <option key={v.label} value={i}>
                {v.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <TryItPanel
        endpoint={endpoint}
        pathParams={pathParams}
        queryParams={queryParams}
        bodySchema={bodySchema}
        rawBodySchema={rawBodySchema}
        schemas={schemas}
        authToken={authToken}
        tenantID={tenantID}
        subtenantID={subtenantID}
        resolveSchema={resolve}
        onCurlChange={handleCurlChange}
        selectedVariantSchema={variants ? variants[selectedVariantIndex]?.schema || null : null}
      />

      {pathParams.length > 0 && (
        <div className="apiref-section">
          <div className="apiref-section-title">Path Parameters</div>
          <div className="apiref-params">
            {pathParams.map((p) => (
              <div key={p.name} className="apiref-param">
                <code className="apiref-param-name">{p.name}</code>
                <span className="apiref-param-type">{(p.schema?.type as string) || 'string'}</span>
                {p.required && <span className="apiref-param-req">required</span>}
                {p.description && <span className="apiref-param-desc">{p.description}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {queryParams.length > 0 && (
        <div className="apiref-section">
          <div className="apiref-section-title">Query Parameters</div>
          <div className="apiref-params">
            {queryParams.map((p) => (
              <div key={p.name} className="apiref-param">
                <code className="apiref-param-name">{p.name}</code>
                <span className="apiref-param-type">{(p.schema?.type as string) || 'string'}</span>
                {p.required && <span className="apiref-param-req">required</span>}
                {p.description && <span className="apiref-param-desc">{p.description}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {bodySchema && (
        <div className="apiref-section">
          <div className="apiref-section-title">Request Body</div>
          <div className="apiref-resp">
            <SchemaViewer schema={bodySchema} />
          </div>
        </div>
      )}

      {responses2xx.length > 0 && (
        <div className="apiref-section">
          <div className="apiref-section-title">Success Responses</div>
          <div className="apiref-responses">
            {responses2xx.map(([code, response]) => {
              const schema = getResponseSchema(response, resolve);

              return (
                <div key={code} className="apiref-resp">
                  <div className="apiref-resp-header">
                    <span className={statusClass(code)}>{code}</span>
                    {response.description && <span className="apiref-resp-desc">{response.description}</span>}
                  </div>
                  {schema && (
                    <div className="apiref-resp-body">
                      <SchemaViewer schema={schema} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {responsesOther.length > 0 && <ErrorResponsesCollapsible responses={responsesOther} resolve={resolve} />}

      {curlCommand && (
        <div className="apiref-section">
          <div className="apiref-section-title">cURL</div>
          <div className="apiref-curl">
            <button type="button" className="apiref-curl-copy" onClick={handleCopyCurl} aria-label="Copy cURL command">
              {curlCopied ? <CheckOutlined style={{ color: '#27ae60' }} /> : <CopyOutlined />}
            </button>
            <CurlCode code={curlCommand} />
          </div>
        </div>
      )}
    </div>
  );
}

function ErrorResponsesCollapsible({
  responses,
  resolve,
}: {
  responses: [string, { description?: string; content?: Record<string, { schema?: Record<string, unknown> }> }][];
  resolve: EndpointDetailProps['resolveSchema'];
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="apiref-section">
      <button type="button" className="apiref-section-toggle" onClick={() => setOpen(!open)}>
        <CaretRightOutlined className={`apiref-section-chevron ${open ? 'open' : ''}`} />
        <span className="apiref-section-title" style={{ marginBottom: 0 }}>
          Error Responses ({responses.length})
        </span>
      </button>
      {open && (
        <div className="apiref-responses" style={{ marginTop: 12 }}>
          {responses.map(([code, response]) => {
            const jsonSchema = response.content?.['application/json']?.schema;
            const schema = jsonSchema ? resolve(jsonSchema) : null;

            return (
              <div key={code} className="apiref-resp">
                <div className="apiref-resp-header">
                  <span className={statusClass(code)}>{code}</span>
                  {response.description && <span className="apiref-resp-desc">{response.description}</span>}
                </div>
                {schema && (
                  <div className="apiref-resp-body">
                    <SchemaViewer schema={schema} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CurlCode({ code }: { code: string }) {
  const parts: React.ReactNode[] = [];
  const regex = /(curl)\b|(-[XHd])\b|(\b(?:GET|POST|PUT|PATCH|DELETE)\b)|('(?:[^'\\]|\\.)*')|(\s*\\\n)/g;
  let lastIndex = 0;
  let key = 0;
  let match = regex.exec(code);

  while (match !== null) {
    if (match.index > lastIndex) {
      parts.push(code.slice(lastIndex, match.index));
    }

    if (match[1])
      parts.push(
        <span key={(key += 1)} className="curl-cmd">
          {match[1]}
        </span>,
      );
    else if (match[2])
      parts.push(
        <span key={(key += 1)} className="curl-flag">
          {match[2]}
        </span>,
      );
    else if (match[3])
      parts.push(
        <span key={(key += 1)} className="curl-method">
          {match[3]}
        </span>,
      );
    else if (match[4])
      parts.push(
        <span key={(key += 1)} className="curl-string">
          {match[4]}
        </span>,
      );
    else if (match[5])
      parts.push(
        <span key={(key += 1)} className="curl-escape">
          {match[5]}
        </span>,
      );

    lastIndex = regex.lastIndex;
    match = regex.exec(code);
  }

  if (lastIndex < code.length) {
    parts.push(code.slice(lastIndex));
  }

  return <pre className="apiref-curl-code">{parts}</pre>;
}
